import {
    ErrorHandler,
    Injectable,
} from '@angular/core';
import { ClientErrorCodes } from '@models';
import { TranslateService } from '@ngx-translate/core';
import {
    Logger,
    ModalService,
} from '@services';
import { UiSnackBarService } from '@uipath/angular/components/ui-snackbar';
import { extractErrorMessage } from '@utils/object.utils';
import { TranslatableError } from '@utils/translatable-error';

export const ERROR_HANDLER_DEPENDENCIES = [
    TranslateService,
    Logger,
    ModalService,
    UiSnackBarService,
];

@Injectable({
    providedIn: 'root',
})
export class ReloadErrorHandler implements ErrorHandler {
    private _isHandled = false;

    constructor(
        private _translateService: TranslateService,
        private _logger: Logger,
        private _modal: ModalService,
        private _snack: UiSnackBarService,
    ) { }

    public handleError(error: any) {
        if (this._isHandled) { return; }

        if (error instanceof TranslatableError) {
            this._handleTranslatableError(error);
            return;
        }

        // handle the unhandled promises :)
        if (error.promise && error.rejection) {
            error = error.rejection;
        }

        // reload the page only when the source isn't
        // coming from a server request (either 4xx, 5xx or timeout — 0)
        const isSourceHttp = error.status && error.status >= 400 || error.status === 0;

        // workaround Chrome bug not showing the error in the correct way
        // this enables again the sourcemaps showing and printing
        // a useful stacktrace
        const stdError = new Error(error.message);
        stdError.name = error.name;
        stdError.stack = error.stack;
        this._logger.error(stdError);

        if (
            !isSourceHttp ||
            error.isSeed
        ) {
            this._isHandled = true;

            // fallback to English for the error message if translations aren't available
            const translations = this._translateWithFallback({
                global_dialog_error_message: `Sorry! An error occurred and the page will reload.`,
            });

            const exceptionMessage = extractErrorMessage(error, '');

            const reloadMessage = `${translations['global_dialog_error_message']}`;

            const message = !exceptionMessage ?
                `${reloadMessage} (#${ClientErrorCodes.UnknownUiError})` :
                `${reloadMessage} (#${ClientErrorCodes.UnknownUiError} - ${exceptionMessage})`;

            this._modal
                .onAlert(message)
                .subscribe(() => window.location.reload());
        } else if (!error.wasShown) {
            const message = extractErrorMessage(
                error,
                `${this._translateService.instant('error_generic')} (#${ClientErrorCodes.GenericError})`,
            );
            this._snack.error(message);
        }
    }

    private _handleTranslatableError(error: TranslatableError) {
        this._isHandled = true;

        this._logger.error(error);

        const baseMessage = this._translateWithFallback({
            global_dialog_error_message: `Sorry! An error occurred and the page will reload.`,
        }).global_dialog_error_message;

        const detailedMessage = this._translateService.instant(error.translationKey);

        const shownMessage = detailedMessage === error.translationKey ?
            `${baseMessage} (#${error.supportCode})` :
            `${baseMessage} (#${error.supportCode}) - ${detailedMessage}`;

        this._modal
            .onAlert(shownMessage)
            .subscribe(() => window.location.reload());
    }

    private _translateWithFallback(translations: Record<string, string>) {
        Object.keys(translations).forEach(key => {
            const translation = this._translateService.instant(key);
            if (translation !== key) {
                translations[key] = translation;
            }
        });

        return translations;
    }
}
